import type { Vec2 } from '../../geo/types';
import type { LifePose, LifeRoute, LifeStep } from './types';

/* A walker rounds a corner within this of it. Its line then passes at most a quarter of this
   inside the corner, which is the margin every cleared step already keeps (routes.ts). */
const CORNER_M = 0.5;

const stepLength = (step: LifeStep): number =>
  Math.hypot(step.to[0] - step.from[0], step.to[1] - step.from[1]);

/* Two walks that meet in the open are one walk round a corner. A doorway is walked straight. */
function cornerBetween(before: LifeStep, after: LifeStep): number {
  if (before.action !== 'walk' || after.action !== 'walk' || before.door || after.door) return 0;
  if (Math.hypot(after.from[0] - before.to[0], after.from[1] - before.to[1]) > 1e-6) return 0;
  return Math.min(CORNER_M, stepLength(before) / 2, stepLength(after) / 2);
}

/* The point `u` of the way round a corner at `at`, entered along `into` and left along `out`,
   and the way the curve runs there. */
function rounded(
  at: Vec2,
  into: LifeStep,
  out: LifeStep,
  reach: number,
  u: number
): { position: Vec2; heading: number } {
  const a = reach / stepLength(into),
    b = reach / stepLength(out);
  const from: Vec2 = [
    at[0] - (into.to[0] - into.from[0]) * a,
    at[1] - (into.to[1] - into.from[1]) * a,
  ];
  const to: Vec2 = [at[0] + (out.to[0] - out.from[0]) * b, at[1] + (out.to[1] - out.from[1]) * b];
  const v = 1 - u;
  return {
    position: [
      v * v * from[0] + 2 * u * v * at[0] + u * u * to[0],
      v * v * from[1] + 2 * u * v * at[1] + u * u * to[1],
    ],
    heading: Math.atan2(
      v * (at[1] - from[1]) + u * (to[1] - at[1]),
      v * (at[0] - from[0]) + u * (to[0] - at[0])
    ),
  };
}

export function routineDuration(route: LifeRoute): number {
  return route.routine?.reduce((sum, step) => sum + step.seconds, 0) ?? 0;
}

/** A finite itinerary includes interior stays, useful stops and travel; its seam is indoors. */
export function routinePoseAt(route: LifeRoute, timeSeconds: number): LifePose {
  const steps = route.routine!;
  const duration = routineDuration(route);
  let time = (((timeSeconds + route.phase) % duration) + duration) % duration;
  let step = steps[0]!,
    index = 0;
  for (const [i, candidate] of steps.entries()) {
    step = candidate;
    index = i;
    if (time < step.seconds) break;
    time -= step.seconds;
  }
  const t = Math.min(1, time / step.seconds);
  const walking = step.action === 'walk';
  const previous = steps[(index + steps.length - 1) % steps.length]!;
  const next = steps[(index + 1) % steps.length]!;
  const weight = walking
    ? Math.max(
        0,
        Math.min(
          1,
          previous.action === 'walk' ? 1 : time / 0.35,
          next.action === 'walk' ? 1 : (step.seconds - time) / 0.35
        )
      )
    : 0;
  const blend = (a: number, b: number, t: number) =>
    a + Math.atan2(Math.sin(b - a), Math.cos(b - a)) * t;
  let facing = step.heading;
  const window = Math.min(0.35, step.seconds / 2);
  if (time < window) facing = blend(previous.heading, step.heading, 0.5 + time / (2 * window));
  else if (step.seconds - time < window)
    facing = blend(step.heading, next.heading, 0.5 - (step.seconds - time) / (2 * window));
  const gait = (timeSeconds * route.speed * Math.PI * 2) / 1.2;
  let position: Vec2 = [
    step.from[0] + (step.to[0] - step.from[0]) * t,
    step.from[1] + (step.to[1] - step.from[1]) * t,
  ];
  if (walking) {
    const length = stepLength(step),
      gone = length * t;
    const entering = cornerBetween(previous, step),
      leaving = cornerBetween(step, next);
    const curve =
      gone < entering
        ? rounded(step.from, previous, step, entering, 0.5 + gone / (2 * entering))
        : length - gone < leaving
          ? rounded(step.to, step, next, leaving, 0.5 - (length - gone) / (2 * leaving))
          : null;
    if (curve) ({ position, heading: facing } = curve);
  }
  return {
    position,
    heading: facing,
    stride: Math.sin(gait) * weight,
    gait,
    gaitWeight: weight,
    walking,
    visible: step.action !== 'inside',
    action: step.action,
    ...(step.prop ? { prop: step.prop } : {}),
    ...(step.door ? { door: step.door } : {}),
    doorOpen:
      step.door !== undefined &&
      (walking || (step.action === 'inside' && step.seconds - time < 1.2)),
  };
}
